import { createClient } from '@supabase/supabase-js'

// Cliente singleton — se crea una sola vez al cargar el módulo.
// No usar función wrapper (getSupabase) porque webpack hot-reload
// re-ejecuta el módulo y el contador estático de GoTrueClient
// incrementa, disparando el warning "Multiple GoTrueClient instances".

// ─── CAPA 3 (interceptor global) ───────────────────────────────────────────────
// Timeout para CUALQUIER request (incluido el refresh de auth). Evita que una
// request suspendida mientras el tab está en background quede colgada para
// siempre y deje "trabado" al cliente (todas las queries siguientes colgadas).
const REQUEST_TIMEOUT_MS = 10000

// Referencia al cliente (se asigna más abajo); las funciones la usan en runtime.
let client = null

// Dedupe de refresh: si varias queries fallan con 401 a la vez, comparten un
// único refreshSession en vuelo (evita reusar el refresh-token de un solo uso).
let refreshInFlight = null
const refreshOnce = () => {
  if (!refreshInFlight) {
    refreshInFlight = client.auth
      .refreshSession()
      .finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

// fetch global con: (1) timeout por request y (2) retry tras refrescar el token
// cuando una query responde 401/403 con JWT vencido. No toca los componentes
// del admin: actúa por debajo de TODAS las llamadas a Supabase.
const fetchWithAuthRetry = async (input, init = {}) => {
  const url = typeof input === 'string' ? input : (input?.url || '')
  const isAuthEndpoint   = url.includes('/auth/v1/')
  // Las subidas a Storage pueden ser legítimamente largas → sin timeout.
  const isStorageUpload  = url.includes('/storage/v1/object')

  const doFetch = (extraInit) => {
    const controller = new AbortController()
    const timer = isStorageUpload
      ? null
      : setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    // Encadenar cualquier AbortSignal externo (p. ej. el de usePagination).
    const external = init.signal
    if (external) {
      if (external.aborted) controller.abort()
      else external.addEventListener('abort', () => controller.abort(), { once: true })
    }
    return fetch(input, { ...init, ...extraInit, signal: controller.signal })
      .finally(() => { if (timer) clearTimeout(timer) })
  }

  let res = await doFetch()

  // Retry solo en requests NO-auth (evita bucle infinito en el endpoint /token).
  if (!isAuthEndpoint && (res.status === 401 || res.status === 403)) {
    try {
      const { data, error } = await refreshOnce()
      if (!error && data?.session?.access_token) {
        const headers = new Headers(init.headers || {})
        headers.set('Authorization', `Bearer ${data.session.access_token}`)
        res = await doFetch({ headers })
      }
      // Si el refresh falla, dejamos pasar el 401 original: GoTrue emitirá
      // SIGNED_OUT y el AuthProvider/ruta protegida llevará al login.
    } catch {
      /* noop — devolvemos la respuesta original */
    }
  }

  return res
}

// ─── CAPA 1 — Cliente Supabase correctamente configurado ───────────────────────
export const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      // Se mantiene 'ae-auth-token' (NO se cambia a 'aeb-...'): cambiar el
      // storageKey desloguea a todos los usuarios con sesión activa una vez y
      // no influye en el bug. Los 3 flags de auth ya están correctos.
      storageKey: 'ae-auth-token',
      // autoRefreshToken DESACTIVADO a propósito: el refresh automático de
      // GoTrue está acoplado al evento visibilitychange y es lo que trababa el
      // cliente al volver de una pestaña en background (cambiar de pestaña
      // disparaba operaciones de auth que colgaban la cadena interna del
      // cliente). En su lugar refrescamos por TIEMPO desde AuthContext (timer)
      // y, ante un 401, desde el fetch global (Capa 3). Así la sesión solo
      // expira por tiempo real y cambiar de pestaña no afecta nada.
      autoRefreshToken: false,
      detectSessionInUrl: true,
      flowType: 'implicit',
      // Lock no-op: GoTrue ya serializa las operaciones de auth DENTRO de la
      // misma pestaña con su flag interno (lockAcquired), así que un no-op no
      // produce refresh concurrentes intra-tab. Evita el deadlock que un lock
      // real con navigator.locks causaba en signInWithPassword. NO eliminar.
      lock: async (name, acquireTimeout, fn) => {
        return await fn()
      },
    },
    global: {
      headers: {
        'x-application-name': 'american-english-web',
      },
      fetch: fetchWithAuthRetry,
    },
  }
)

// Asignar la referencia usada por el interceptor (refreshOnce).
client = supabase

/**
 * Sube un archivo al Storage de Supabase con verificación de sesión y retry automático.
 * @param {File} file - Archivo a subir
 * @param {string} fileName - Nombre del archivo en el bucket
 * @param {string} bucket - Nombre del bucket (default: 'media')
 * @returns {Promise<{publicUrl: string}>}
 */
export const uploadToStorage = async (file, fileName, bucket = 'media') => {
  // Verificar sesión activa antes de subir
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) {
    throw new Error('Sin sesión activa. Iniciá sesión de nuevo.')
  }

  const uploadOpts = { cacheControl: '3600', upsert: true }

  let { error } = await supabase.storage
    .from(bucket)
    .upload(fileName, file, uploadOpts)

  // Si falla por JWT inválido, refrescar sesión y reintentar una vez
  if (error && (error.message?.includes('Invalid JWT') || error.message?.includes('not authorized'))) {
    await supabase.auth.refreshSession()
    const retry = await supabase.storage
      .from(bucket)
      .upload(fileName, file, uploadOpts)
    if (retry.error) throw retry.error
  } else if (error) {
    throw error
  }

  const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(fileName)
  return { publicUrl }
}

/**
 * Elimina un objeto del Storage de Supabase a partir de su URL pública.
 * Extrae el path posterior a '/object/public/<bucket>/' y llama a remove().
 * @param {string} publicUrl - URL pública del archivo (publicUrl de uploadToStorage)
 * @param {string} bucket - Nombre del bucket (default: 'media')
 * @returns {Promise<void>}
 * @throws {Error} si la URL no es válida o si el borrado falla
 */
export const deleteFromStorage = async (publicUrl, bucket = 'media') => {
  if (!publicUrl) {
    throw new Error('URL inválida: no se puede eliminar la imagen.')
  }
  // El path del objeto está después de '/object/public/<bucket>/'.
  const marker = `/object/public/${bucket}/`
  const idx = publicUrl.indexOf(marker)
  if (idx === -1) {
    throw new Error('No se pudo determinar la ruta del archivo a eliminar.')
  }
  const path = decodeURIComponent(publicUrl.slice(idx + marker.length))

  const { error } = await supabase.storage.from(bucket).remove([path])
  if (error) throw error
}
